import * as Yup from "yup";

import { Modal, OfficeButton } from "components";
import {
  UpdateGiftStatus,
  UpdateGiftStatusVariables,
} from "_graphql/mutation/__generated__/UpdateGiftStatus";
import UpdatePointRedemptionForm, {
  UpdatePointRedemptionFormValues,
} from "components/forms/update-points-redemption";
import { useEffect, useRef } from "react";

import { UPDATE_POINT_REDEMPTION_STATUS } from "_graphql/visal/mutations/point-redemption";
import toast from "react-hot-toast";
import { useAppSearch } from "_graphql/cache/auth";
import { useFormik } from "formik";
import { useMutation } from "@apollo/client";
import { usePointRedemptionUsage } from "hooks/data/use-point-redemptions";
import { wrapClick } from "utils";

const validationSchema = Yup.object().shape({
  status: Yup.string()
    .required("Status is required")
    .oneOf(["RECEIVED", "PENDING", "IN-PROGRESS"], "Invalid status"),
  redemptionId: Yup.string().required("Redemption ID is required"),
});

export default function UpdatePointRedemptionContainer({
  open,
  setOpen,
}: {
  open: boolean;
  setOpen: (val: boolean) => void;
}) {
  const searchParams = useAppSearch();
  const { loading, redemption, refetch } = usePointRedemptionUsage({
    redemptionId: searchParams?.id || "",
  });

  // Use a ref to track if we've initialized the form
  const initialized = useRef(false);

  const [execute, { loading: updating }] = useMutation<
    UpdateGiftStatus,
    UpdateGiftStatusVariables
  >(UPDATE_POINT_REDEMPTION_STATUS, {
    refetchQueries: [
      "fetchPointRedemptionUsage",
      "getAllPointRedemptionUsages",
    ],
  });

  const form = useFormik<UpdatePointRedemptionFormValues>({
    initialValues: {
      status: "",
      redemptionId: searchParams?.id || "",
    },
    enableReinitialize: false,
    validateOnChange: true,
    validationSchema,
    onSubmit: async (variables, helpers) => {
      execute({
        variables: {
          status: {
            id: variables.redemptionId,
            status: variables.status,
          },
        },
      })
        .then(({ data }) => {
          if (data?.updateGiftStatus) {
            toast.success("Redemption status updated successfully");
            setOpen(false);
            helpers.resetForm();
            refetch();
          } else {
            toast.error("Failed to update redemption status");
          }
        })
        .catch((err) => {
          toast.error(err.message);
        });
    },
    onReset: () => {
      setOpen(false);
    },
  });

  // Only set values once when redemption data is available and not already initialized
  useEffect(() => {
    if (redemption && !initialized.current && open) {
      form.setValues({
        status: redemption.status || "",
        redemptionId: searchParams?.id || "",
      });
      initialized.current = true;
    }

    // Reset the initialization flag when the modal closes
    if (!open) {
      initialized.current = false;
    }
  }, [redemption, searchParams?.id, open]);

  return (
    <Modal
      open={open}
      setOpen={setOpen}
      loading={loading}
      title="Update Redemption Status"
      description="Update the status of this point redemption"
      renderActions={() => (
        <OfficeButton
          type="button"
          size="sm"
          loading={updating}
          disabled={updating}
          onClick={wrapClick(form.handleSubmit)}
        >
          {updating ? "Please wait..." : "Update Status"}
        </OfficeButton>
      )}
    >
      <UpdatePointRedemptionForm form={form} />
    </Modal>
  );
}
